/* eslint-disable no-restricted-imports */
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { Trans, useTranslation } from 'react-i18next';
import type { TransFn, TranslateFunction } from './i18nHooks.types';

export type { TranslateFunction };

function generateI18NHook<N extends string>(namespace: N): () => TranslateFunction<N> {
	return () => {
		const keyPrefix = namespace.split('.').at(-1);
		const { t } = useTranslation(namespace as string, { keyPrefix });

		return t as TranslateFunction<N>;
	};
}

function generateI18NTransComponent(namespace: string): TransFn {
	const fn: TransFn & { displayName?: string } = function (props) {
		const { children, ...restProps } = props;

		const t = generateI18NHook(namespace)();

		return (
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			<Trans {...restProps} t={t}>
				{children}
			</Trans>
		);
	};

	fn.displayName = `Trans${namespace}`;

	return fn;
}

export const useTranslationAuth = generateI18NHook(I18Namespaces.AUTH);
export const useTranslationAdminDashboard = generateI18NHook(I18Namespaces.ADMIN_DASHBOARD);
export const useTranslationCapManage = generateI18NHook(I18Namespaces.CAP_MANAGE);
export const useTranslationCapTable = generateI18NHook(I18Namespaces.CAP_TABLE);
export const useTranslationCapChart = generateI18NHook(I18Namespaces.CAP_CHART);
export const useTranslationCommonForm = generateI18NHook(I18Namespaces.COMMON_FORM);
export const useTranslationCommonUi = generateI18NHook(I18Namespaces.COMMON_UI);
export const useTranslationDefault = generateI18NHook(I18Namespaces.DEFAULT);
export const useTranslationDocuments = generateI18NHook(I18Namespaces.DOCUMENT_LIBRARY);
export const useTranslationMipMessages = generateI18NHook(I18Namespaces.MIP_MESSAGES);
export const useTranslationInvestor = generateI18NHook(I18Namespaces.INVESTOR);
export const useTranslationMipCreate = generateI18NHook(I18Namespaces.MIP_CREATE);
export const useTranslationMipManage = generateI18NHook(I18Namespaces.MIP_MANAGE);
export const useTranslationNotFound = generateI18NHook(I18Namespaces.NOT_FOUND);
export const useTranslationTerms = generateI18NHook(I18Namespaces.TERMS);
export const useTranslationExports = generateI18NHook(I18Namespaces.EXPORTS);
export const useTranslationUserProfiles = generateI18NHook(I18Namespaces.USER_PROFILES);
export const useTranslationOrganization = generateI18NHook(I18Namespaces.ORGANIZATION);
export const useTranslationValuations = generateI18NHook(I18Namespaces.VALUATIONS);
export const useTranslationHelpBlog = generateI18NHook(I18Namespaces.HELP_BLOG);

export const TransCapManage = generateI18NTransComponent(I18Namespaces.CAP_MANAGE);
export const TransInvestor = generateI18NTransComponent(I18Namespaces.INVESTOR);
export const TransMipCreate = generateI18NTransComponent(I18Namespaces.MIP_CREATE);
export const TransMipManage = generateI18NTransComponent(I18Namespaces.MIP_MANAGE);
export const TransTerms = generateI18NTransComponent(I18Namespaces.TERMS);
export const TransValuations = generateI18NTransComponent(I18Namespaces.VALUATIONS);
export const useCurrentLanguage = () => {
	const { i18n } = useTranslation();
	return i18n.language;
};
